import { MODAL_NONE } from '@components/App/Modal/constants'
import { useScaleEvents } from '@components/ArtBoard/Canvas/hooks/events'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import {
  PROJECT_HISTORY_REDO_REQUEST,
  PROJECT_HISTORY_UNDO_REQUEST,
} from '@store/reducers/manager/middleware/history/types'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { AppState } from '@/types'
import { allowEventTarget } from '@/utils/allowEventTarget'
import { Keys } from '../keyboard'

const useOnCanvasKeyboardActions = () => {
  const dispatch = useDispatch()

  const { onZoomIn, onZoomOut, onSetZoom, onFit } = useScaleEvents()

  const modalType = useSelector<AppState, string>(({ ui }) => ui.modalType)

  // history selectors
  const canUndo = useSelector<AppState, boolean>(({ ui }) => ui.undo)

  const canRedo = useSelector<AppState, boolean>(({ ui }) => ui.redo)

  const { edit, seoMode, simpleView } = useArtboardMode()
  const readOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const artboardDisabled = useArtBoardDisabled()

  const showStructure = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.showStructure
  )

  const historyActions = (event: KeyboardEvent) => {
    if (!event.shiftKey && canUndo) {
      event.preventDefault()

      dispatch({ type: PROJECT_HISTORY_UNDO_REQUEST })
    }

    if (event.shiftKey && canRedo) {
      event.preventDefault()

      dispatch({ type: PROJECT_HISTORY_REDO_REQUEST })
    }
  }

  const zoomIn = (event: KeyboardEvent) => {
    event.preventDefault()

    onZoomIn()
  }

  const zoomOut = (event: KeyboardEvent) => {
    event.preventDefault()

    onZoomOut()
  }

  const zoomReset = (event: KeyboardEvent) => {
    event.preventDefault()

    onSetZoom(1)
  }

  const zoomFit = (event: KeyboardEvent) => {
    event.preventDefault()

    onFit()
  }

  const search = (event: KeyboardEvent) => {
    event.preventDefault()

    if (showStructure) {
      return
    }

    dispatch(
      artBoardActions.showStructure({
        showStructure: true,
      })
    )
  }

  return (event: KeyboardEvent) => {
    const targetEditable = allowEventTarget(event)

    // Hide Blocks (OCT-5138) — это тот же редактор с компактными нодами:
    // undo/redo, зум и поиск в нём работают так же, как в edit.
    if (modalType !== MODAL_NONE || (!edit && !seoMode && !simpleView)) {
      return
    }

    if (!targetEditable) {
      return
    }

    switch (event.code) {
      case Keys.Z: {
        if (
          (event.ctrlKey || event.metaKey) &&
          !readOnly &&
          !artboardDisabled
        ) {
          historyActions(event)
        }

        return
      }

      case Keys.ZoomIn: {
        if (event.ctrlKey || event.metaKey) {
          zoomIn(event)
        }

        return
      }

      case Keys.ZoomOut: {
        if (event.ctrlKey || event.metaKey) {
          zoomOut(event)
        }

        return
      }

      case Keys.ZoomReset: {
        if (event.ctrlKey || event.metaKey) {
          zoomReset(event)
        }

        return
      }

      case Keys.Search: {
        if ((edit || simpleView) && (event.ctrlKey || event.metaKey)) {
          search(event)
        }

        return
      }

      case Keys.ZoomFit: {
        if (event.shiftKey) {
          zoomFit(event)
        }

        return
      }

      default: {
        return null
      }
    }
  }
}

export default useOnCanvasKeyboardActions
